/**
 * Public Booking Clinic Landing Page
 *
 * Route: /book/c/:clinicSlug  (optionally ?p=<practitionerSlug>)
 *
 * Shows the clinic's available appointment types so the patient can pick one.
 * Selecting a type forwards to the existing /book/c/:clinicSlug/t/:typeSlug
 * V2 flow (preserving the practitioner preselection if present).
 */

import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertCircle, Clock, Video, MapPin, ChevronRight } from 'lucide-react';
import {
  publicBookingV2Service,
  type ClinicLandingPage,
} from '@/services/bookingService';

const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins === 0 ? `${hours} hr` : `${hours} hr ${mins} min`;
};

const PublicBookingClinicLandingPage: React.FC = () => {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const [searchParams] = useSearchParams();
  const practitionerSlug = searchParams.get('p');

  const [pageData, setPageData] = useState<ClinicLandingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clinicSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicBookingV2Service
      .getClinicLandingPage(clinicSlug, practitionerSlug)
      .then(data => {
        if (!cancelled) setPageData(data);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || 'This booking page is not available.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clinicSlug, practitionerSlug]);

  if (loading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white'>
        <div className='text-center'>
          <Loader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-teal-600' />
          <p className='text-slate-500'>Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 p-4'>
        <Card className='w-full max-w-md shadow-lg'>
          <CardContent className='pt-8 text-center'>
            <AlertCircle className='mx-auto mb-4 h-16 w-16 text-red-500' />
            <h1 className='mb-2 text-2xl font-bold text-slate-900'>Booking Unavailable</h1>
            <p className='text-slate-600'>{error || 'This booking page is not available.'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { clinic, practitioner, appointment_types: types } = pageData;
  const providerDisplayName = practitioner ? `Dr. ${practitioner.full_name}` : null;

  const buildHref = (typeSlug: string) => {
    const base = `/book/c/${clinic.booking_slug}/t/${typeSlug}`;
    return practitionerSlug ? `${base}?p=${encodeURIComponent(practitionerSlug)}` : base;
  };

  return (
    <div className='min-h-screen bg-slate-50'>
      <div className='mx-auto max-w-2xl px-4 py-10 sm:py-16'>
        <header className='mb-8 text-center'>
          <h1 className='text-3xl font-bold text-slate-900 sm:text-4xl'>{clinic.name}</h1>
          {providerDisplayName && (
            <p className='mt-2 text-lg text-slate-600'>
              {providerDisplayName}
              {practitioner?.credentials ? `, ${practitioner.credentials}` : ''}
            </p>
          )}
          {practitioner?.specialization && (
            <p className='text-sm text-slate-500'>{practitioner.specialization}</p>
          )}
          <p className='mt-4 text-sm text-slate-600'>
            Choose an appointment type to get started.
          </p>
        </header>

        <div className='space-y-3'>
          {types.map(type => (
            <Link
              key={type.id}
              to={buildHref(type.booking_slug)}
              className='block rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2'
            >
              <Card className='relative overflow-hidden transition-all hover:border-teal-300 hover:shadow-md'>
                <div
                  className='absolute left-0 top-0 h-full w-1'
                  style={{ backgroundColor: type.color || '#0D9488' }}
                />
                <CardContent className='flex items-center gap-4 py-5 pl-6 pr-4'>
                  <div className='min-w-0 flex-1'>
                    <h2 className='font-semibold text-slate-900'>{type.name}</h2>
                    {type.description && (
                      <p className='mt-1 line-clamp-2 text-sm text-slate-500'>{type.description}</p>
                    )}
                    <div className='mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600'>
                      <div className='flex items-center gap-1'>
                        <Clock className='h-4 w-4 text-slate-400' />
                        <span>{formatDuration(type.duration_minutes)}</span>
                      </div>
                      {type.telehealth_enabled && (
                        <div className='flex items-center gap-1'>
                          <Video className='h-4 w-4 text-teal-500' />
                          <span>Video</span>
                        </div>
                      )}
                      {type.in_person_enabled && (
                        <div className='flex items-center gap-1'>
                          <MapPin className='h-4 w-4 text-blue-500' />
                          <span>In-person</span>
                        </div>
                      )}
                      {type.price_cents && type.price_cents > 0 ? (
                        <Badge className='border-emerald-200 bg-emerald-100 font-medium text-emerald-700'>
                          ${(type.price_cents / 100).toFixed(2)}
                        </Badge>
                      ) : (
                        <Badge variant='outline' className='font-medium text-slate-500'>
                          Free
                        </Badge>
                      )}
                    </div>
                  </div>
                  <ChevronRight className='h-5 w-5 flex-shrink-0 text-slate-400' />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingClinicLandingPage;
